// Helper para llamar los endpoints nuevos api/v2/* (Etapa 7 del backend),
// que exigen autorización real vía token firmado — a diferencia de las
// rutas legacy (api/evaluations/*, etc.), que el resto del frontend sigue
// llamando exactamente igual, sin token.
//
// El token se guarda en localStorage bajo la clave "authToken" desde
// Login.js. TTL = SESSION_TIMEOUT_MINUTES del backend (por defecto 30 min).
// startTokenAutoRefresh() lo renueva antes de expirar mientras la app esté abierta.

function getApiBaseUrl() {
  return process.env.REACT_APP_API_BASE_URL || "";
}

function getAuthToken() {
  try {
    return localStorage.getItem("authToken");
  } catch (_) {
    return null;
  }
}

function setAuthToken(token) {
  try {
    if (token) localStorage.setItem("authToken", token);
  } catch (_) { /* ignore */ }
}

/** Decodifica el payload del token HMAC (sin verificar firma; solo lectura de exp). */
export function leerPayloadToken(token) {
  if (!token || typeof token !== "string") return null;
  const partes = token.split(".");
  if (partes.length !== 2) return null;
  try {
    const padded = partes[0].replace(/-/g, "+").replace(/_/g, "/");
    const padLen = (4 - (padded.length % 4)) % 4;
    const json = atob(padded + "=".repeat(padLen));
    return JSON.parse(json);
  } catch (_) {
    return null;
  }
}

let refreshEnCurso = null;
let refreshTimerId = null;

/**
 * Renueva el token si sigue siendo válido. Evita llamadas concurrentes.
 * @returns {Promise<string|null>} Nuevo token o null si falló.
 */
export async function refreshAuthToken() {
  const token = getAuthToken();
  if (!token) return null;

  if (refreshEnCurso) return refreshEnCurso;

  refreshEnCurso = (async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v2/auth/refresh`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data?.success || !data?.data?.token) {
        return null;
      }
      setAuthToken(data.data.token);
      return data.data.token;
    } catch (_) {
      return null;
    } finally {
      refreshEnCurso = null;
    }
  })();

  return refreshEnCurso;
}

/**
 * Si el token expira en menos de `margenSegundos`, lo renueva.
 * Por defecto 5 minutos de margen.
 */
export async function refreshAuthTokenSiHaceFalta(margenSegundos = 300) {
  const token = getAuthToken();
  if (!token) return null;
  const payload = leerPayloadToken(token);
  if (!payload || !payload.exp) return null;

  const segundosRestantes = Number(payload.exp) - Math.floor(Date.now() / 1000);
  if (segundosRestantes > margenSegundos) {
    return token;
  }
  // Ya expiró: no se puede renovar sin re-login
  if (segundosRestantes <= 0) {
    return null;
  }
  return refreshAuthToken();
}

/**
 * Programa renovaciones periódicas mientras haya sesión.
 * Se llama desde App.js cuando el usuario está autenticado.
 */
export function startTokenAutoRefresh() {
  stopTokenAutoRefresh();

  const tick = async () => {
    if (!getAuthToken()) return;
    await refreshAuthTokenSiHaceFalta(300);
  };

  tick();
  refreshTimerId = setInterval(tick, 60 * 1000);

  const onVisibility = () => {
    if (document.visibilityState === "visible") tick();
  };
  document.addEventListener("visibilitychange", onVisibility);
  startTokenAutoRefresh._onVisibility = onVisibility;
}

export function stopTokenAutoRefresh() {
  if (refreshTimerId) {
    clearInterval(refreshTimerId);
    refreshTimerId = null;
  }
  if (startTokenAutoRefresh._onVisibility) {
    document.removeEventListener("visibilitychange", startTokenAutoRefresh._onVisibility);
    startTokenAutoRefresh._onVisibility = null;
  }
}

/**
 * @param {string} path Ruta relativa empezando en "api/v2/..." (sin barra inicial).
 * @param {object} [options] Opciones estándar de fetch (method, body, etc.).
 * @returns {Promise<{ok: boolean, status: number, data: any}>}
 */
export async function fetchV2(path, options = {}) {
  // Renovar en silencio si está por vencer (no aplica al propio refresh)
  if (!String(path).includes("auth/refresh")) {
    await refreshAuthTokenSiHaceFalta(300);
  }

  const token = getAuthToken();
  const headers = {
    Accept: "application/json",
    ...(options.body ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const response = await fetch(`${getApiBaseUrl()}/${path.replace(/^\/+/, "")}`, {
    ...options,
    headers,
  });

  let data = null;
  try {
    data = await response.json();
  } catch (_) {
    data = null;
  }

  return { ok: response.ok, status: response.status, data };
}

export function tieneTokenV2() {
  return Boolean(getAuthToken());
}
